/* eslint-disable no-undef-init */
import React, {
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react';
import Map, {
	Layer,
	MapRef,
	Marker,
	NavigationControl,
	Source,
} from 'react-map-gl';
import type { FeatureCollection } from 'geojson';
import mapboxgl from 'utils/mapboxgl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Button, Spinner, Typography } from 'design_system/src';
import { useNavigate } from 'react-router-dom';

import {
	InterestPointType,
	InterestPointTypeValues,
} from 'models/repossession/enums/repossession.enums';
import Supercluster from 'supercluster';
import { momentLocal } from 'models/shared/helpers/dates';
import { ReactComponent as Search } from 'design_system/src/static/icons/search.svg';
import { ReactComponent as Moto } from 'design_system/src/static/icons/motorcycle.svg';
import { ReactComponent as Checkbox } from 'design_system/src/static/icons/checkmark.svg';
import { ReactComponent as Eye } from 'design_system/src/static/icons/eye.svg';

import { fetchTilesLocationHistory } from 'models/geozon/controllers/tile.controller';
import { TileTrackerHistoryLocationDto } from 'models/geozon/interfaces/tile.interface';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import {
	hotspotHeatLayerDefinition,
	hotspotLayerDefinition,
} from 'views/devicesMap/DevicesMap.constants';

import { repossessionContext } from '../../RepossessionsContext';
import {
	activeInterestPointTypes,
	CreateRouteSteps,
	PlainLocation,
} from '../../Repossessions.constants';

import '../../recovery.scss';

export const AllRepossessionsMap = () => {
	const mapRef = useRef<MapRef>(null);
	const navigate = useNavigate();

	const {
		initialPoints,
		selectedMarkers,
		setSelectedMarkers,
		createRouteStep,
		setCreateRouteStep,
		noPointRepossessions,
		loading,
		clearContext,
		repossessionsData,
	} = useContext(repossessionContext);

	const [query, setQuery] = useState('');

	const [historyPoint, setHistoryPoint] = useState<PlainLocation | null>(null);
	const [history, setHistory] = useState<TileTrackerHistoryLocationDto[]>([]);
	const [loadingHistory, setLoadingHistory] = useState(false);

	const [viewState, setViewState] = useState({
		longitude: -99.12766,
		latitude: 19.42847,
		zoom: 10,
	});

	const supercluster = useMemo(
		() =>
			new Supercluster({
				radius: 75,
				maxZoom: 20,
			}),
		[],
	);

	const [interestPointTypeFilter, setInterestPointTypeFilter] = useState<
		Partial<Record<InterestPointType, boolean>>
	>({
		[InterestPointType.address]: true,
		[InterestPointType.geolocation]: true,
		[InterestPointType.signalTileVehicle]: true,
		[InterestPointType.noSignalTileVehicle]: true,
	});

	const [onlySelected, setOnlySelected] = useState(false);

	const markers = useMemo(() => {
		if (!initialPoints) return [];
		const initialPointsFiltered = initialPoints.filter(
			(point) =>
				query.split(',').some((word) => point.vehicleId.includes(word)) &&
				interestPointTypeFilter[point.locationType] &&
				(!onlySelected || selectedMarkers.has(point.loanId)),
		);
		supercluster.load(
			initialPointsFiltered.map((point) => ({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.coordinates.long, point.coordinates.lat],
				},
				properties: point,
			})),
		);

		return supercluster
			.getClusters([-180, -85, 180, 85], Math.round(viewState.zoom))
			.map((cluster) => {
				const [longitude, latitude] = cluster.geometry.coordinates;

				if (cluster.properties.cluster) {
					return (
						<Marker
							key={`cluster-${cluster.id}`}
							longitude={longitude}
							latitude={latitude}
							onClick={() => handleClusterClick(cluster)}
						>
							<div className="cluster_1">
								<div className="cluster_2">
									<div className="cluster_3 dim_xxxl">
										<Typography
											weight="600"
											scale="small"
											className="cluster_count"
										>
											{cluster.properties.point_count}
										</Typography>
									</div>
								</div>
							</div>
						</Marker>
					);
				}
				const point = cluster.properties as PlainLocation;
				const Icon = InterestPointTypeValues[point.locationType].icon;
				const selected = selectedMarkers.has(point.loanId);
				return (
					<Marker
						key={point.vehicleId}
						longitude={longitude}
						latitude={latitude}
						onClick={() => {
							onClickHandler(point);
						}}
					>
						<div
							className={`br_xxl main_marker cursor_pointer ${
								InterestPointTypeValues[point.locationType].color
							} ${selected ? 'selected_marker' : ''}`}
						>
							<div className="marker_info p_y_xxs p_x_md flex_center flex_gap_sm">
								<Icon className="dim_lg icon" />
								<Typography weight="600" scale="small">
									{point.vehicleId}
								</Typography>
							</div>
							<div className="marker_details p_x_md flex_center_col">
								<Typography weight="600" scale="xsmall">
									{momentLocal(point.createdAt).format('DD/MM/YYYY')}
								</Typography>
								<Typography weight="400" scale="xsmall">
									{momentLocal(point.createdAt).format('HH:mm')}
								</Typography>
							</div>
							<span className="arrow" />
						</div>
					</Marker>
				);
			});
	}, [
		initialPoints,
		supercluster,
		viewState.zoom,
		selectedMarkers,
		createRouteStep,
		query,
		interestPointTypeFilter,
		onlySelected,
	]);

	const handleClusterClick = useCallback(
		(cluster) => {
			const clusterExpansionZoom = supercluster.getClusterExpansionZoom(
				cluster.id,
			);
			mapRef.current?.flyTo({
				center: cluster.geometry.coordinates,
				zoom: clusterExpansionZoom,
			});
		},
		[supercluster, viewState],
	);

	const openHistory = (point: PlainLocation) => {
		setHistoryPoint(point);
		setHistory([]);
		const tileId = repossessionsData[point.loanId]?.tileUuid;
		if (!tileId) return;
		setLoadingHistory(true);
		fetchTilesLocationHistory(tileId)
			.then((data) => {
				setHistory(
					[...data.locations].sort(
						(a, b) =>
							new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
					),
				);
			})
			.catch(commonErrorHandler)
			.finally(() => setLoadingHistory(false));
	};

	const closeHistory = () => {
		setHistoryPoint(null);
		setHistory([]);
	};

	const onClickHandler = (point: PlainLocation) => {
		if (createRouteStep) {
			setSelectedMarkers((prevSelectedItems) => {
				const newSelectedItems = new Set(prevSelectedItems);
				if (newSelectedItems.has(point.loanId)) {
					newSelectedItems.delete(point.loanId);
				} else {
					newSelectedItems.add(point.loanId);
				}
				return newSelectedItems;
			});
			return;
		}
		openHistory(point);
	};

	const historyLayer = useMemo(() => {
		if (!history.length) return null;
		const source: FeatureCollection = {
			type: 'FeatureCollection',
			features: history.map((point, i) => ({
				id: `hist_${i}`,
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: [point.longitude, point.latitude],
				},
				properties: {
					weight: 6 / history.length,
					point,
				},
			})),
		};
		return (
			<Source type="geojson" data={source}>
				<Layer {...hotspotHeatLayerDefinition} />
				<Layer {...hotspotLayerDefinition} />
			</Source>
		);
	}, [history]);

	const goToVehicle = (e: any, internalId?: string) => {
		e.stopPropagation();
		if (internalId) {
			navigate(`/inventory/${internalId}`);
		}
	};

	return (
		<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
			<div className="repossession_map_input_cont ">
				<input
					type="text"
					className="repossession_map_input shadow_hard"
					placeholder="Buscar Moto"
					value={query}
					onChange={(e) => setQuery(e.target.value.toUpperCase())}
				/>
				<Search className="repossession_map_search_icon dim_lg text_primary_300" />
			</div>
			<div className="repossession_map_legend">
				<div className=" bg_neutral_0 br_md p_md m_b_md">
					{Object.entries(InterestPointTypeValues)
						.filter(([key]) =>
							activeInterestPointTypes.includes(key as InterestPointType),
						)
						.map(([key, type]) => (
							<div
								key={key}
								className="display_flex flex_align_center flex_gap_xs flex_justify_between"
							>
								<div className="display_flex flex_align_center flex_gap_xs">
									<type.icon className={` text_${type.realColor} dim_xl`} />
									<Typography scale="small" weight="600">
										{type.title}
									</Typography>
								</div>
								<div
									className={`repossession_map_input_checkbox br_xs m_l_md ${
										interestPointTypeFilter[key as InterestPointType]
											? 'selected'
											: ''
									}`}
									onClick={() =>
										setInterestPointTypeFilter({
											...interestPointTypeFilter,
											[key]: !interestPointTypeFilter[key as InterestPointType],
										})
									}
									onKeyDown={() =>
										setInterestPointTypeFilter({
											...interestPointTypeFilter,
											[key]: !interestPointTypeFilter[key as InterestPointType],
										})
									}
									role="checkbox"
									aria-checked={
										interestPointTypeFilter[key as InterestPointType]
									}
									tabIndex={0}
								>
									<Checkbox className="repossession_map_input_checkbox_icon center" />
								</div>
							</div>
						))}
					<div className="display_flex flex_align_center flex_gap_xs flex_justify_between">
						<div className="display_flex flex_align_center flex_gap_xs">
							<Eye className="text_primary_300 dim_xl" />
							<Typography scale="small" weight="600">
								Ver solo seleccionadas
							</Typography>
						</div>
						<div
							className={`repossession_map_input_checkbox br_xs m_l_md ${
								onlySelected ? 'selected' : ''
							}`}
							onClick={() => setOnlySelected(!onlySelected)}
							onKeyDown={() => setOnlySelected(!onlySelected)}
							role="checkbox"
							aria-checked={onlySelected}
							tabIndex={0}
						>
							<Checkbox className="repossession_map_input_checkbox_icon center" />
						</div>
					</div>
				</div>
				{noPointRepossessions.length ? (
					<div className="bg_neutral_0 br_md ">
						<div className="p_md border_b_solid border_neutral_300 border_1">
							<Typography
								scale="small"
								weight="600"
								className="text_center w_100_per"
							>
								Vehículos sin información disponible
							</Typography>
						</div>
						<div className="overflow_y_scroll no_info_vehicles">
							{noPointRepossessions.map((point) => (
								<div className="p_md border_b_solid border_neutral_300 border_1">
									<Typography
										scale="small"
										weight="600"
										className="text_center text_link w_100_per"
										onClick={(e) => goToVehicle(e, point)}
									>
										{point}
									</Typography>
								</div>
							))}
						</div>
					</div>
				) : null}
			</div>
			{loading && (
				<div className="pos_absolute center_x br_md bg_neutral_0 p_md z_index_30 m_t_xl flex_center flex_gap_md">
					<Spinner className="dim_xxl" />
					<div>
						<Typography scale="medium" weight="600" className="m_b_xxs">
							Cargando mapa...
						</Typography>
						<Typography scale="small" weight="400" className="">
							📍 No tardaremos mucho.
						</Typography>
					</div>
				</div>
			)}
			{createRouteStep && (
				<>
					<div className="repossession_map_counter flex_center">
						<div className="repossession_map_counter_icon p_md bg_primary_300 br_circle display_flex">
							<Moto className="dim_xl text_neutral_0" />
						</div>
						<div className="p_y_sm p_l_xl bg_neutral_0 br_md">
							<Typography scale="small" weight="400" className="p_r_lg p_l_xs">
								Motos seleccionadas: {selectedMarkers.size}
							</Typography>
						</div>
					</div>
					<Button
						className="repossession_map_prev_button"
						onClick={() => clearContext()}
					>
						Limpiar ruta
					</Button>
					<Button
						className="repossession_map_next_button"
						disabled={selectedMarkers.size < 1}
						onClick={() =>
							setCreateRouteStep(CreateRouteSteps.filterRepossessions)
						}
					>
						Continuar
					</Button>
				</>
			)}
			{historyPoint && (
				<div className="repossession_history_panel bg_neutral_0 shadow_hard br_md">
					<div className="display_flex flex_justify_between flex_align_center p_md border_b_solid border_neutral_300 border_1">
						<Typography scale="small" weight="600">
							Puntos históricos · {historyPoint.vehicleId}
						</Typography>
						<Typography
							scale="small"
							weight="600"
							className="text_link cursor_pointer"
							onClick={closeHistory}
						>
							Cerrar
						</Typography>
					</div>
					<div className="repossession_history_list overflow_y_scroll">
						{loadingHistory && (
							<div className="p_md flex_center">
								<Spinner className="dim_xl" />
							</div>
						)}
						{!loadingHistory && !history.length && (
							<Typography
								scale="small"
								weight="400"
								className="p_md text_center"
							>
								Sin puntos históricos para este vehículo.
							</Typography>
						)}
						{!loadingHistory &&
							history.map((point, i) => (
								<div
									key={`${point.timestamp}_${i}`}
									className="p_sm border_b_solid border_neutral_300 border_1 cursor_pointer"
									onClick={() =>
										mapRef.current?.flyTo({
											center: [point.longitude, point.latitude],
											zoom: 15,
										})
									}
									onKeyDown={() => null}
									role="button"
									tabIndex={0}
								>
									<Typography scale="small" weight="600">
										{momentLocal(point.timestamp).format('DD/MM/YYYY')}
									</Typography>
									<Typography
										scale="xsmall"
										weight="400"
										textColor="neutral_700"
									>
										{momentLocal(point.timestamp).format('HH:mm')}
									</Typography>
								</div>
							))}
					</div>
				</div>
			)}
			<Map
				mapLib={mapboxgl}
				mapboxAccessToken={process.env.REACT_APP_MAPBOX_API_KEY || ''}
				onMove={(evt) => setViewState(evt.viewState)}
				mapStyle="mapbox://styles/mapbox/streets-v9"
				reuseMaps
				ref={mapRef}
				{...viewState}
			>
				<NavigationControl
					position="bottom-right"
					style={{ marginBottom: '6rem' }}
				/>
				{markers}
				{historyLayer}
			</Map>
		</div>
	);
};
